import {
  memo,
  ProviderProps,
  useLayoutEffect,
  useMemo,
  useCallback,
  useContext,
  useState,
} from 'react';
import { useTranslate } from '@tolgee/react';
import styled from '@emotion/styled';
import Icon, { CloseCircleFilled, LeftOutlined, RightOutlined } from '@ant-design/icons';
import { noop } from 'lodash-es';
import createNamedContext from 'utils/createNamedContext';
import useModal from 'hooks/useModal';
import ImageSlide from './ImageSlide';
import useSlide from './useSlide';
import SiderModal, { SiderModalProps } from './SiderModal';
import { useSiderRefContext } from './RefContext';
import { Image as _Image, Button } from 'antd';
import { useVideoText } from '../Chat/components/VideoText';
import { ReactComponent as Video2textSvg } from '../Chat/components/MessageMenu/assets/video2text.svg';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';

const Modal = styled(SiderModal)`
  .${({ theme }) => theme.getAntdPrefixedClassName('modal')} {
    &-content {
      background-color: transparent;
      box-shadow: none;
    }
    &-body {
      display: flex;
      flex-direction: column;
      align-items: center;
      padding: 0;
      > * {
        margin-bottom: 16px;
      }
    }
  }
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 220px;
  height: 32px;
  padding: 0 8px;
  font-size: 12px;
  color: var(--fett-text-secondary);
  background: var(--fett-bg-panel);
  box-shadow: 0 4px 10px 0 #00000019;
`;

const Action = styled.span<{ disabled: boolean }>`
  user-select: none;
  ${({ disabled }) =>
    disabled
      ? `
    color: #E4E7EC;
    cursor: not-allowed;
  `
      : `
    cursor: pointer;
    &:hover {
      color: var(--fett-scenario-accent-text)
    }
  `}
  > span {
    margin: 0 5px;
    font-size: 10px;
  }
`;

const Close = styled(CloseCircleFilled)`
  font-size: 20px;
  color: #d3d7e0;
  cursor: pointer;
  &:hover {
    color: #adceff;
  }
`;

const Image = styled(_Image)`
  object-fit: contain;
  height: 100%;
`;

export interface PreviewModalProps extends SiderModalProps {
  imageFirst?: boolean;
  images?: string[];
  videos?: string[];
  initialIndex?: number;
  width?: number;
  extra?: {
    platform: General.PlatformUpperCaseTunnel;
    region: General.UpperCaseRegion;
    storeId: string;
    tradeId: string;
  };
}

function PreviewModal(props: PreviewModalProps) {
  const {
    imageFirst,
    images = [],
    videos = [],
    initialIndex = 0,
    visible,
    onCancel = noop,
    width,
    extra,
    ...otherProps
  } = props;

  const { t } = useTranslate();

  const { whiteListConfig: { ASR } = {} } = useOrgConfigStateValue();

  const length = images.length + videos.length;

  const { index, preDisabled, nextDisabled, pre, next, setIndex } = useSlide({
    count: 1,
    length,
    initialIndex,
  });

  useLayoutEffect(() => {
    if (visible) {
      setIndex(initialIndex);
    }
  }, [visible, setIndex, initialIndex]);

  const slide = (
    <ImageSlide
      imageFirst={imageFirst}
      images={images}
      videos={videos}
      count={1}
      index={index}
      offsetPercent={0}
      imageRender={(src, props) => <Image preview src={src} {...props} />}
    />
  );

  const preAction = (
    <Action disabled={preDisabled} onClick={pre}>
      <LeftOutlined />
      {t('scene.chatWorkspace.orderTab.preview.action.previous')}
    </Action>
  );

  const nextAction = (
    <Action disabled={nextDisabled} onClick={next}>
      {t('scene.chatWorkspace.orderTab.preview.action.next')}
      <RightOutlined />
    </Action>
  );

  const toolbar = (
    <Toolbar>
      {preAction}
      <div>{`${index + 1}/${length}`}</div>
      {nextAction}
    </Toolbar>
  );

  const close = <Close onClick={onCancel} />;

  const videoTextList =
    ASR &&
    extra &&
    videos.map((video, i) => {
      return (
        <VideoText video={video} hide={index !== (imageFirst ? images.length + i : i)} {...extra} />
      );
    });

  return (
    <Modal
      visible={visible}
      closable={false}
      title={null}
      footer={null}
      width={width || 320}
      onCancel={onCancel}
      destroyOnClose
      {...otherProps}
    >
      {slide}
      {videoTextList}
      {toolbar}
      {close}
    </Modal>
  );
}

const MemorizedPreviewModal = memo(PreviewModal);

function VideoText({
  video,
  hide,
  platform,
  region,
  storeId,
  tradeId,
}: {
  video: string;
  hide: boolean;
  platform: General.PlatformUpperCaseTunnel;
  region: General.UpperCaseRegion;
  storeId: string;
  tradeId: string;
}) {
  const { t } = useTranslate();

  const [videoText, setVideoText] = useState<string>();

  const { translateContent, translateTrigger } = useVideoText({
    region,
    videoText,
    setVideoText,
    getVideoAsrTextBody: useCallback(
      () =>
        Promise.resolve({
          marketplace: platform,
          region,
          storeId,
          source: 'refund',
          sourceId: tradeId,
          videoPath: video,
        }),
      [platform, region, storeId, tradeId, video],
    ),
  });

  if (!videoText) {
    return (
      <Container style={hide ? { display: 'none' } : undefined} className="flex justify-end">
        <Button loading={videoText !== undefined} onClick={() => setVideoText('')}>
          {t('scene.chatWorkspace.orderTab.preview.action.videoToText')}
        </Button>
      </Container>
    );
  }

  return (
    <Container style={hide ? { display: 'none' } : undefined}>
      <p>{videoText}</p>
      {translateContent}
      {translateTrigger}
      <Icon component={Video2textSvg} />
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  width: 100%;
  margin-top: -16px;
  padding: 12px;
  background: var(--fett-bg-panel);
  word-break: break-all;
  overflow-wrap: break-word;
  white-space: pre-line;
  > p {
    margin-bottom: 8px;
  }
  > div {
    padding: 8px 0;
    border-top: 1px solid #ececec;
  }
  > .anticon {
    position: absolute;
    top: 0;
    right: 0;
    background: var(--fett-bg-info-tip);
  }
  > span {
    display: inline-flex;
  }
`;

export interface PreviewModalContextValue {
  show: (meta?: PreviewModalProps) => void;
  hide: (meta?: PreviewModalProps) => void;
}

export const PreviewModalContext = createNamedContext<PreviewModalContextValue | null>(
  'PreviewModalContext',
  null,
);

export function PreviewModalProvider(
  props: Omit<ProviderProps<PreviewModalContextValue>, 'value'>,
) {
  const { children, ...otherProps } = props;

  const siderRef = useSiderRefContext();

  const { visible, show, hide, meta } = useModal<PreviewModalProps>();

  const providerValue = useMemo(() => {
    return {
      show,
      hide,
    };
  }, [show, hide]);

  const onCancel = useCallback(() => {
    hide();
  }, [hide]);

  return (
    <PreviewModalContext.Provider value={providerValue} {...otherProps}>
      {meta ? (
        <MemorizedPreviewModal
          visible={visible}
          sider={siderRef.current || undefined}
          onCancel={onCancel}
          {...meta}
        />
      ) : null}
      {children}
    </PreviewModalContext.Provider>
  );
}

export function usePreviewModalContext() {
  const value = useContext(PreviewModalContext);

  if (value === null) {
    throw new Error(
      'Hook usePreviewModalContext should be used in a context of PreviewModalProvider component.',
    );
  }

  return value;
}

export default MemorizedPreviewModal;
